/**
 * Utility functions for API calls with proper error handling
 */

/**
 * Safely parse JSON response, handling HTML error responses
 * @param {Response} response - Fetch API response object
 * @returns {Promise<any>} Parsed JSON data or throws an error
 */
export const safeJsonParse = async (response) => {
  const contentType = response.headers.get('content-type');
  
  // Check if response is HTML (likely an error page)
  if (contentType && contentType.includes('text/html')) {
    const text = await response.text();
    throw new Error(`Received HTML response instead of JSON. Status: ${response.status}`);
  }
  
  // Check if response is JSON
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  }
  
  // If no content-type or unknown type, try to parse as JSON
  let text = '';
  try {
    text = await response.text();
    if (!text) {
      return {};
    }
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Failed to parse response as JSON. Content: ${text.substring(0, 100)}...`);
  }
};

/**
 * Enhanced fetch wrapper with better error handling
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data or throws an error
 */
export const apiFetch = async (url, options = {}) => {
  try {
    // Build headers and avoid unnecessary Content-Type on GET to prevent CORS preflight
    const headers = { ...(options.headers || {}) };
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      headers,
      ...options
    });

    if (!response.ok) {
      // Try to parse error response
      try {
        const errorData = await safeJsonParse(response);
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      } catch (parseError) {
        // If we can't parse the error response, throw a generic error
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    }

    // Parse successful response
    const data = await safeJsonParse(response);
    return data;
  } catch (error) {
    // Re-throw with more context
    throw new Error(`API request failed: ${error.message}`);
  }
};

/**
 * Check if a response is successful and contains valid JSON
 * @param {Response} response - Fetch API response object
 * @returns {boolean} True if response is OK and JSON, false otherwise
 */
export const isSuccessfulJsonResponse = async (response) => {
  if (!response.ok) return false;
  
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return false;
  }
  
  try {
    await response.clone().json();
    return true;
  } catch {
    return false;
  }
};